import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { ReportCard } from "../components/ReportCard";
import { ReportDetailModal } from "../components/ReportDetailModal";
import { CrewMap } from "../components/CrewMap";
import { LinkTeammate } from "../components/LinkTeammate";
import { PLATFORM_COLORS } from "../lib/constants";
import { Loader2, Repeat, Gavel, Send, ArrowLeft, ShieldAlert, Users, X } from "lucide-react";
import { toast } from "sonner";

export default function PlayerProfile() {
  const { platform, gamertag } = useParams();
  const navigate = useNavigate();
  const { user, openAuth, isMod } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [crewKey, setCrewKey] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/players/${platform}/${encodeURIComponent(gamertag)}`);
      setProfile(data);
    } catch {
      setProfile(null);
    } finally {
      setLoading(false);
    }
  }, [platform, gamertag]);

  useEffect(() => { load(); }, [load]);

  const handleUpvote = async (report) => {
    if (!user) return openAuth();
    try {
      const { data } = await api.post(`/reports/${report.id}/upvote`);
      setProfile((p) => ({ ...p, reports: p.reports.map((r) => (r.id === data.id ? { ...data, is_serial: p.is_serial } : r)) }));
      if (selected?.id === data.id) setSelected(data);
    } catch { toast.error("Vote failed."); }
  };

  const handleReact = async (report, emoji) => {
    if (!user) return openAuth();
    try {
      const { data } = await api.post(`/reports/${report.id}/react`, { emoji });
      setProfile((p) => ({ ...p, reports: p.reports.map((r) => (r.id === data.id ? { ...data, is_serial: p.is_serial } : r)) }));
      if (selected?.id === data.id) setSelected(data);
    } catch { toast.error("Reaction failed."); }
  };

  const unlinkTeammate = async (t) => {
    if (!profile?.primary_report_id) return;
    const rid = profile.primary_report_id;
    try {
      const { data } = await api.post(`/reports/${rid}/unlink-teammate`, { platform: t.platform, gamertag: t.gamertag });
      await load();
      setCrewKey((k) => k + 1);
      toast.success(`Unlinked ${t.gamertag} from ${profile.gamertag}.`, data.unlinked_report_id ? {
        action: {
          label: "Undo",
          onClick: async () => {
            try {
              await api.post(`/reports/${rid}/link-teammate`, { teammate_report_ids: [data.unlinked_report_id] });
              await load();
              setCrewKey((k) => k + 1);
              toast.success(`Re-linked ${t.gamertag}.`);
            } catch { toast.error("Could not undo the unlink."); }
          },
        },
      } : undefined);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not unlink teammate.");
    }
  };

  if (loading) return <div className="flex justify-center py-32 text-[#64748B]"><Loader2 className="animate-spin" /></div>;

  if (!profile) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-24 text-center">
        <ShieldAlert className="mx-auto mb-3 text-[#334155]" size={40} />
        <h1 className="font-heading text-2xl font-bold text-white">No public reports for this player</h1>
        <p className="mt-2 text-[#94A3B8]">{gamertag} ({platform}) has no vetted reports on the wall.</p>
        <button onClick={() => navigate("/")} className="mt-6 inline-flex items-center gap-1 text-[#00F0FF] hover:underline"><ArrowLeft size={15} /> Back to the wall</button>
      </div>
    );
  }

  const platColor = PLATFORM_COLORS[profile.platform] || "#94A3B8";

  return (
    <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6">
      <button onClick={() => navigate(-1)} className="mb-5 inline-flex items-center gap-1 text-sm text-[#94A3B8] hover:text-white"><ArrowLeft size={15} /> Back</button>

      <div className="animate-fade-up overflow-hidden rounded-2xl border border-[#1E293F] bg-[#0F1424]">
        <div className="grid-texture border-b border-[#1E293F] bg-[#141B2D] p-6">
          <div className="flex flex-wrap items-center gap-2">
            {profile.is_banned && <span className="inline-flex items-center gap-1 rounded-full bg-[#FF2A55]/15 px-2.5 py-1 text-xs font-bold uppercase text-[#FF2A55]"><Gavel size={12} /> Confirmed Cheater</span>}
            {profile.sent_to_dev && !profile.is_banned && <span className="inline-flex items-center gap-1 rounded-full bg-[#00FF87]/15 px-2.5 py-1 text-xs font-bold uppercase text-[#00FF87]"><Send size={12} /> Sent to 2K</span>}
            {profile.is_serial && <span className="inline-flex items-center gap-1 rounded-full bg-[#FF2A55] px-2.5 py-1 text-xs font-bold uppercase text-white"><Repeat size={12} /> Serial Offender</span>}
          </div>
          <h1 className="mt-3 font-heading text-4xl font-black tracking-tight text-white" data-testid="player-gamertag">{profile.gamertag}</h1>
          <span className="mt-1 inline-block rounded px-2 py-0.5 font-mono text-sm font-semibold" style={{ color: platColor, backgroundColor: `${platColor}18` }}>{profile.platform}</span>
          {profile.tracked_name && profile.tracked_name !== profile.gamertag && (
            <div className="mt-2 flex items-center gap-1.5 text-sm font-semibold text-[#F97316]" data-testid="player-now-known-as">
              <Repeat size={14} /> now goes by <span className="text-white">{profile.tracked_name}</span>
            </div>
          )}
        </div>

        {profile.aliases?.length > 0 && (
          <div className="border-b border-[#1E293F] p-5" data-testid="player-alias-history">
            <h3 className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-[#F97316]"><Repeat size={13} /> Alias History <span className="text-[#64748B]">({profile.aliases.length})</span></h3>
            <p className="mt-1 text-xs text-[#64748B]">Every name this account has used — they can't hide by renaming.</p>
            <ul className="mt-3 space-y-1.5 text-sm">
              {profile.aliases.map((a, i) => (
                <li key={i} className="flex flex-wrap items-center gap-2" data-testid={`alias-row-${i}`}>
                  <span className="text-xs text-[#64748B]">{new Date(a.at).toLocaleDateString()}</span>
                  <span className="rounded bg-[#141B2D] px-2 py-0.5 text-[#94A3B8] line-through decoration-[#64748B]">{a.from}</span>
                  <Repeat size={12} className="text-[#F97316]" />
                  <span className="rounded bg-[#F97316]/15 px-2 py-0.5 font-semibold text-[#FDBA74]">{a.to}</span>
                  {a.source?.startsWith("auto") && <span className="rounded bg-[#00F0FF]/15 px-1.5 py-0.5 text-[9px] font-bold uppercase text-[#00F0FF]">auto-detected</span>}
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="grid grid-cols-2 divide-x divide-[#1E293F] border-b border-[#1E293F] sm:grid-cols-4">
          <div className="p-5 text-center"><div className="font-heading text-2xl font-black text-white">{profile.total_reports}</div><div className="text-xs uppercase text-[#64748B]">Reports</div></div>
          <div className="p-5 text-center"><div className="font-heading text-2xl font-black text-[#FF2A55]">{profile.total_faced}</div><div className="text-xs uppercase text-[#64748B]">Players Faced</div></div>
          <div className="p-5 text-center"><div className="font-heading text-sm font-bold text-[#94A3B8]">{new Date(profile.first_seen).toLocaleDateString()}</div><div className="text-xs uppercase text-[#64748B]">First Seen</div></div>
          <div className="p-5 text-center"><div className="font-heading text-sm font-bold text-[#94A3B8]">{new Date(profile.last_seen).toLocaleDateString()}</div><div className="text-xs uppercase text-[#64748B]">Last Seen</div></div>
        </div>

        <div className="flex flex-wrap gap-1.5 p-5">
          <span className="text-xs text-[#64748B]">Offenses:</span>
          {profile.cheat_types.map((c) => (
            <span key={c} className="rounded border border-[#FF2A55]/30 bg-[#FF2A55]/10 px-2 py-0.5 text-xs font-semibold text-[#FF2A55]">{c}</span>
          ))}
        </div>
      </div>

      {(profile.runs_with?.length > 0 || (isMod && profile.primary_report_id)) && (
        <div className="mt-6 animate-fade-up rounded-2xl border border-[#7C3AED]/25 bg-[#7C3AED]/[0.06] p-5" data-testid="player-crew">
          <h2 className="flex items-center gap-1.5 font-heading text-lg font-bold text-[#C084FC]"><Users size={17} /> The Crew</h2>
          <p className="mt-1 text-xs text-[#94A3B8]">Accounts caught running with {profile.gamertag}. Link more without opening a case.</p>
          {profile.runs_with?.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2" data-testid="player-runs-with">
              {profile.runs_with.map((t, i) => (
                <span key={i} className="inline-flex items-center gap-1 rounded-full border border-[#7C3AED]/40 bg-[#7C3AED]/10 pl-3 pr-1.5 py-1 text-sm font-semibold text-[#C084FC]">
                  <button data-testid={`player-runs-with-link-${i}`}
                    onClick={() => navigate(`/players/${encodeURIComponent(t.platform)}/${encodeURIComponent(t.gamertag)}`)}
                    className="transition-colors hover:text-white">
                    {t.gamertag} <span className="text-xs font-normal text-[#94A3B8]">· {t.platform}</span>
                  </button>
                  {isMod && (
                    <button data-testid={`player-runs-with-unlink-${i}`} title={`Unlink ${t.gamertag}`}
                      onClick={() => unlinkTeammate(t)}
                      className="rounded-full p-0.5 text-[#94A3B8] transition-colors hover:bg-[#FF2A55]/20 hover:text-[#FF2A55]">
                      <X size={13} />
                    </button>
                  )}
                </span>
              ))}
            </div>
          )}
          {isMod && profile.primary_report_id && (
            <LinkTeammate reportId={profile.primary_report_id} currentGamertag={profile.gamertag}
              onLinked={() => { load(); setCrewKey((k) => k + 1); }} />
          )}
          {profile.runs_with?.length > 0 && profile.primary_report_id && (
            <CrewMap key={crewKey} reportId={profile.primary_report_id} />
          )}
        </div>
      )}

      <h2 className="mt-8 font-heading text-xl font-bold text-white">Report Timeline ({profile.reports.length})</h2>
      <div className="mt-4 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {profile.reports.map((r, i) => (
          <ReportCard key={r.id} report={{ ...r, is_serial: profile.is_serial }} index={i} onUpvote={handleUpvote} onOpen={setSelected} />
        ))}
      </div>

      <ReportDetailModal report={selected} open={!!selected} onClose={() => setSelected(null)} onUpvote={handleUpvote} onReact={handleReact} />
    </div>
  );
}
